import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 3000;

/* ---------- Phone formatting helpers ---------------------------------- */
const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (!digits) return "";
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isValidPhone = (formatted) => formatted.replace(/\D/g, "").length === 10;

const formatElapsed = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const displayInboundNumber = (raw) => {
    if (!raw) return "";
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
};

/* ---------------------------------------------------------------------- */

const AgentLiveDemo = ({ agentKey, agentName, open, minimized, onOpen, onClose, onMinimize }) => {
    // phases: intake | connecting | active | waiting-transcript | transcript
    const [phase, setPhase] = useState("intake");
    const [mode, setMode] = useState(null); // 'inbound' | 'outbound'
    const [inboundNumber, setInboundNumber] = useState(null);
    const [ready, setReady] = useState(false);
    const [configError, setConfigError] = useState(null);

    const [phone, setPhone] = useState("");
    const [touched, setTouched] = useState(false);
    const [startError, setStartError] = useState(null);
    const [copied, setCopied] = useState(false);

    const [conversationId, setConversationId] = useState(null);
    const [openedAt, setOpenedAt] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [transcript, setTranscript] = useState(null);
    const [callDuration, setCallDuration] = useState(0);

    const timerRef = useRef(null);
    const pollRef = useRef(null);
    const scrollRef = useRef(null);
    const [portalTarget, setPortalTarget] = useState(null);

    useEffect(() => {
        if (typeof document !== "undefined") setPortalTarget(document.body);
    }, []);

    const valid = useMemo(() => isValidPhone(phone), [phone]);

    /* Fetch config when opened for the first time */
    useEffect(() => {
        if (!open || !agentKey || mode) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/api/live-demo/config/${agentKey}`);
                const data = await res.json();
                if (cancelled) return;
                setMode(data.mode);
                setInboundNumber(data.inbound_number);
                setReady(!!data.ready);
                setOpenedAt(new Date().toISOString());
                if (data.mode === "inbound") {
                    setPhase("waiting-transcript");
                }
            } catch (err) {
                if (!cancelled) setConfigError("Couldn't load the demo. Try again later.");
            }
        })();
        return () => { cancelled = true; };
    }, [open, agentKey, mode]);

    /* Reset all state when fully closed */
    useEffect(() => {
        if (!open && !minimized) {
            setPhase("intake");
            setMode(null);
            setInboundNumber(null);
            setReady(false);
            setConfigError(null);
            setPhone("");
            setTouched(false);
            setStartError(null);
            setConversationId(null);
            setOpenedAt(null);
            setElapsed(0);
            setTranscript(null);
            setCallDuration(0);
        }
    }, [open, minimized]);

    /* Escape closes modal when safe (not mid-call) */
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape" && (phase === "intake" || phase === "transcript")) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, phase, onClose]);

    /* Elapsed timer — runs during connecting/active/waiting-transcript */
    useEffect(() => {
        const running = phase === "connecting" || phase === "active" || phase === "waiting-transcript";
        if (!running) {
            clearInterval(timerRef.current);
            return undefined;
        }
        timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
        return () => clearInterval(timerRef.current);
    }, [phase]);

    /* Poll for transcript */
    const stopPolling = useCallback(() => {
        clearInterval(pollRef.current);
        pollRef.current = null;
    }, []);

    useEffect(() => {
        const pollingOutbound = phase === "active" && conversationId;
        const pollingInbound = phase === "waiting-transcript" && mode === "inbound" && openedAt;
        if (!pollingOutbound && !pollingInbound) {
            stopPolling();
            return undefined;
        }
        let cancelled = false;
        const poll = async () => {
            try {
                const url = pollingOutbound
                    ? `${API}/api/live-demo/transcript/${conversationId}`
                    : `${API}/api/live-demo/inbound-latest/${agentKey}?since=${encodeURIComponent(openedAt)}`;
                const res = await fetch(url);
                const data = await res.json();
                if (cancelled) return;
                if (data.status === "ready" && Array.isArray(data.transcript) && data.transcript.length > 0) {
                    setTranscript(data.transcript);
                    setCallDuration(data.call_duration_secs || 0);
                    setConversationId(data.conversation_id || conversationId);
                    setPhase("transcript");
                    stopPolling();
                }
            } catch (err) {
                console.error("[AgentLiveDemo] transcript poll failed", err);
            }
        };
        pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
        poll();
        return () => { cancelled = true; stopPolling(); };
    }, [phase, conversationId, mode, agentKey, openedAt, stopPolling]);

    /* Auto-scroll transcript when it renders */
    useEffect(() => {
        if (phase === "transcript" && scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [phase]);

    const placeOutboundCall = async () => {
        if (!valid) {
            setTouched(true);
            return;
        }
        setStartError(null);
        setPhase("connecting");
        try {
            const res = await fetch(`${API}/api/live-demo/call`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agent_key: agentKey, to_number: phone.replace(/\D/g, "") }),
            });
            const data = await res.json();
            if (!res.ok) {
                setStartError(data.detail || "Couldn't place the call. Try again in a moment.");
                setPhase("intake");
                return;
            }
            setConversationId(data.conversation_id);
            setPhase("active");
        } catch (err) {
            setStartError("Network error. Try again in a moment.");
            setPhase("intake");
        }
    };

    const copyInboundNumber = async () => {
        if (!inboundNumber) return;
        try {
            await navigator.clipboard.writeText(inboundNumber);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (err) {
            console.error("[AgentLiveDemo] clipboard copy failed", err);
        }
    };

    if (!portalTarget) return null;

    /* ---------------------------------------------------------------- */
    /*  Minimized floating pill                                          */
    /* ---------------------------------------------------------------- */
    const minimizedPill = (
        <AnimatePresence>
            {minimized && (
                <motion.button
                    type="button"
                    onClick={onOpen}
                    data-testid="live-demo-minimized-pill"
                    initial={{ opacity: 0, y: 40, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 40, scale: 0.9 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] group inline-flex items-center gap-3 pl-3 pr-5 py-2.5 rounded-full bg-[var(--ink)] text-white shadow-[0_10px_40px_-8px_rgba(0,0,0,0.45)] hover:shadow-[0_16px_50px_-8px_rgba(30,91,255,0.55)] transition-shadow"
                >
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-70 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--blue)]" />
                    </span>
                    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/90">
                        {phase === "transcript" ? "Transcript ready" : "Call in progress"}
                    </span>
                    <span className="text-white/40">·</span>
                    <span className="font-mono text-[13px] tabular-nums text-white">
                        {formatElapsed(elapsed)}
                    </span>
                    <i className="fa-solid fa-up-right-and-down-left-from-center text-[10px] text-white/50 group-hover:text-white ml-1 transition-colors" />
                </motion.button>
            )}
        </AnimatePresence>
    );

    /* ---------------------------------------------------------------- */
    /*  Header title (per phase)                                         */
    /* ---------------------------------------------------------------- */
    let title = agentName;
    if (phase === "intake" && mode === "outbound") title = "We'll call you";
    if (phase === "connecting") title = "Placing the call…";
    if (phase === "active") title = "Call in progress";
    if (phase === "waiting-transcript") title = mode === "inbound" ? "Give us a call" : "Wrapping up…";
    if (phase === "transcript") title = "Call transcript";

    /* ---------------------------------------------------------------- */
    /*  Full modal                                                       */
    /* ---------------------------------------------------------------- */
    const modal = (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-6"
                    data-testid="live-demo-modal"
                >
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={phase === "intake" || phase === "transcript" ? onClose : undefined}
                        className="absolute inset-0 bg-[var(--ink)]/60 backdrop-blur-md cursor-default"
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.98 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-[560px] rounded-2xl bg-[var(--paper)] border border-[var(--border-strong)] shadow-[0_30px_90px_-20px_rgba(0,0,0,0.35)] overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-[var(--blue-soft)] accent-blue flex items-center justify-center flex-shrink-0">
                                    <i className={`fa-solid ${mode === "inbound" ? "fa-phone-volume" : "fa-phone-flip"} text-[13px]`} />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dim)] truncate">
                                        Live demo · {agentName}
                                    </div>
                                    <div className="font-display text-[15px] font-semibold text-[var(--ink)] leading-tight mt-0.5">
                                        {title}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                {(phase === "active" || phase === "waiting-transcript") && (
                                    <button
                                        type="button"
                                        onClick={onMinimize}
                                        data-testid="live-demo-minimize"
                                        aria-label="Minimize"
                                        className="w-8 h-8 rounded-lg text-[var(--ink-soft)] hover:text-[var(--ink)] hover:bg-[var(--cream-2)] transition-colors flex items-center justify-center"
                                    >
                                        <i className="fa-solid fa-minus text-[12px]" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    data-testid="live-demo-close"
                                    aria-label="Close"
                                    className="w-8 h-8 rounded-lg text-[var(--ink-soft)] hover:text-[var(--ink)] hover:bg-[var(--cream-2)] transition-colors flex items-center justify-center"
                                >
                                    <i className="fa-solid fa-xmark text-[13px]" />
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-6">
                            {configError && (
                                <div className="text-[13px] text-red-500 py-6 text-center">{configError}</div>
                            )}

                            {!ready && !configError && mode && (
                                <div className="py-6 text-center">
                                    <div className="w-12 h-12 mx-auto rounded-full bg-[var(--cream-2)] flex items-center justify-center mb-3">
                                        <i className="fa-solid fa-wrench accent-blue text-lg" />
                                    </div>
                                    <div className="font-display text-[15px] font-semibold text-[var(--ink)]">
                                        Demo coming soon
                                    </div>
                                    <p className="mt-2 text-[13px] text-[var(--ink-soft)]">
                                        This agent&apos;s live demo is being provisioned. Check back shortly — or book a call and we&apos;ll walk you through it live.
                                    </p>
                                </div>
                            )}

                            {/* ---------------- INBOUND ---------------- */}
                            {ready && mode === "inbound" && phase === "waiting-transcript" && (
                                <div>
                                    <p className="text-[14px] text-[var(--ink-soft)] leading-relaxed">
                                        Call this number to talk to our <span className="text-[var(--ink)] font-medium">{agentName}</span> agent. Hang up when you&apos;re done — the transcript will appear here.
                                    </p>

                                    <div className="mt-5 rounded-2xl border border-[var(--border-strong)] bg-[var(--cream)] p-5">
                                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dim)] mb-2">
                                            Call this number
                                        </div>
                                        <div className="flex items-center justify-between gap-4">
                                            <a
                                                href={`tel:${inboundNumber}`}
                                                data-testid="live-demo-inbound-number"
                                                className="font-display text-[26px] md:text-[30px] font-semibold text-[var(--ink)] tracking-tight tabular-nums hover:accent-blue transition-colors"
                                            >
                                                {displayInboundNumber(inboundNumber)}
                                            </a>
                                            <button
                                                type="button"
                                                onClick={copyInboundNumber}
                                                data-testid="live-demo-copy-number"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[12px] font-medium text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white hover:border-[var(--ink)] transition-colors"
                                            >
                                                <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} text-[10px]`} />
                                                {copied ? "Copied" : "Copy"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mt-5 flex items-center gap-2 text-[12px] font-mono text-[var(--ink-dim)] tracking-[0.14em] uppercase">
                                        <span className="relative flex h-1.5 w-1.5">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-70 animate-ping" />
                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--blue)]" />
                                        </span>
                                        Waiting for your call · {formatElapsed(elapsed)}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={onMinimize}
                                        data-testid="live-demo-minimize-cta"
                                        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] font-medium text-[13px] hover:bg-[var(--cream-2)] transition-colors"
                                    >
                                        <i className="fa-solid fa-minus text-[10px]" />
                                        Minimize
                                    </button>
                                </div>
                            )}

                            {/* ---------------- OUTBOUND — intake ---------------- */}
                            {ready && mode === "outbound" && phase === "intake" && (
                                <div>
                                    <p className="text-[14px] text-[var(--ink-soft)] leading-relaxed">
                                        Drop your number — the <span className="text-[var(--ink)] font-medium">{agentName}</span> agent will call you in a few seconds. Hang up when you&apos;re done to see the transcript.
                                    </p>
                                    <div className="mt-5">
                                        <label
                                            htmlFor="demo-phone"
                                            className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dim)] block mb-2"
                                        >
                                            Your phone number
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-dim)]">
                                                <i className="fa-solid fa-phone text-[13px]" />
                                            </span>
                                            <input
                                                id="demo-phone"
                                                type="tel"
                                                inputMode="numeric"
                                                autoComplete="tel"
                                                value={phone}
                                                onChange={(e) => setPhone(formatPhone(e.target.value))}
                                                onBlur={() => setTouched(true)}
                                                onKeyDown={(e) => e.key === "Enter" && placeOutboundCall()}
                                                placeholder="(555) 123-4567"
                                                data-testid="live-demo-phone-input"
                                                className={`w-full pl-11 pr-4 py-3.5 rounded-xl border bg-[var(--cream)] text-[15px] font-mono tabular-nums text-[var(--ink)] placeholder:text-[var(--ink-dim)]/60 focus:outline-none focus:ring-2 transition-all ${
                                                    (touched && !valid) || startError
                                                        ? "border-red-400/60 focus:ring-red-400/30"
                                                        : "border-[var(--border-strong)] focus:border-[var(--blue)] focus:ring-[var(--blue)]/25"
                                                }`}
                                            />
                                        </div>
                                        {touched && !valid && (
                                            <p className="mt-2 text-[12px] text-red-500 flex items-center gap-1.5">
                                                <i className="fa-solid fa-circle-exclamation text-[11px]" />
                                                Enter a valid 10-digit US number.
                                            </p>
                                        )}
                                        {startError && (
                                            <p className="mt-2 text-[12px] text-red-500 flex items-center gap-1.5">
                                                <i className="fa-solid fa-circle-exclamation text-[11px]" />
                                                {startError}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={placeOutboundCall}
                                        data-testid="live-demo-start"
                                        disabled={!valid && touched}
                                        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[var(--ink)] text-white font-medium text-[14px] hover:bg-[var(--blue)] transition-colors disabled:opacity-50"
                                    >
                                        <i className="fa-solid fa-phone text-[11px]" />
                                        Call me now
                                    </button>
                                </div>
                            )}

                            {/* ---------------- OUTBOUND — connecting ---------------- */}
                            {phase === "connecting" && (
                                <div className="py-10 flex flex-col items-center gap-4">
                                    <div className="relative flex h-14 w-14">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-30 animate-ping" />
                                        <span className="relative inline-flex rounded-full h-14 w-14 bg-[var(--blue-soft)] accent-blue items-center justify-center">
                                            <i className="fa-solid fa-phone text-lg" />
                                        </span>
                                    </div>
                                    <div className="text-center">
                                        <div className="font-display text-[15px] font-semibold text-[var(--ink)]">
                                            Dialing {phone}…
                                        </div>
                                        <div className="mt-1 font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--ink-dim)]">
                                            Warming the agent
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ---------------- OUTBOUND — active call ---------------- */}
                            {phase === "active" && (
                                <div>
                                    <div className="rounded-2xl border border-[var(--border-strong)] bg-[var(--cream)] p-6 flex flex-col items-center gap-4">
                                        {/* Animated mic-wave */}
                                        <div className="flex items-end gap-1 h-14">
                                            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                                                <motion.span
                                                    key={i}
                                                    className="w-1.5 rounded-full bg-[var(--blue)]"
                                                    animate={{ height: ["18%", "100%", "35%"] }}
                                                    transition={{ duration: 0.9, delay: i * 0.08, repeat: Infinity, ease: "easeInOut" }}
                                                />
                                            ))}
                                        </div>
                                        <div className="text-center">
                                            <div className="font-mono text-[10px] tracking-[0.22em] uppercase accent-blue">
                                                Live · calling {phone}
                                            </div>
                                            <div className="mt-2 font-display text-[32px] font-semibold text-[var(--ink)] tabular-nums">
                                                {formatElapsed(elapsed)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-5 flex items-start gap-3 rounded-xl bg-[var(--cream-2)] border border-[var(--border)] px-4 py-3">
                                        <i className="fa-solid fa-mobile-screen-button text-[13px] accent-blue mt-0.5" />
                                        <div className="text-[12px] leading-relaxed text-[var(--ink-soft)]">
                                            The call is live on your phone. <span className="text-[var(--ink)] font-medium">Hang up when you&apos;re done</span> — the transcript appears here after the call.
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={onMinimize}
                                        data-testid="live-demo-minimize-cta"
                                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] font-medium text-[13px] hover:bg-[var(--cream-2)] transition-colors"
                                    >
                                        <i className="fa-solid fa-minus text-[10px]" />
                                        Minimize
                                    </button>
                                </div>
                            )}

                            {/* ---------------- TRANSCRIPT ---------------- */}
                            {phase === "transcript" && transcript && (
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dim)]">
                                            Transcript · {formatElapsed(callDuration || elapsed)}
                                        </div>
                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.14em] uppercase accent-blue">
                                            <i className="fa-solid fa-check text-[10px]" />
                                            Complete
                                        </span>
                                    </div>
                                    <div
                                        ref={scrollRef}
                                        data-testid="live-demo-transcript"
                                        className="h-[320px] overflow-y-auto rounded-xl bg-[var(--cream)] border border-[var(--border)] p-4 space-y-3"
                                    >
                                        {transcript.map((line, i) => (
                                            <div
                                                key={`${line.role}-${line.time_in_call_secs ?? i}-${(line.text || "").slice(0, 24)}`}
                                                className={`flex ${line.role === "agent" ? "justify-start" : "justify-end"}`}
                                            >
                                                <div
                                                    className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-snug ${
                                                        line.role === "agent"
                                                            ? "bg-[var(--paper)] border border-[var(--border)] text-[var(--ink)] rounded-bl-md"
                                                            : "bg-[var(--ink)] text-white rounded-br-md"
                                                    }`}
                                                >
                                                    <div
                                                        className={`font-mono text-[9px] tracking-[0.2em] uppercase mb-1 ${
                                                            line.role === "agent" ? "accent-blue" : "text-white/50"
                                                        }`}
                                                    >
                                                        {line.role === "agent" ? "Agent" : "You"}
                                                    </div>
                                                    {line.text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        data-testid="live-demo-done"
                                        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[var(--ink)] text-white font-medium text-[13px] hover:bg-[var(--blue)] transition-colors"
                                    >
                                        Done
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(
        <>
            {modal}
            {minimizedPill}
        </>,
        portalTarget,
    );
};

export default AgentLiveDemo;
